import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  HeartIcon, MailIcon, EyeIcon, EyeOffIcon, LockIcon,
  UserIcon, PhoneIcon, GoogleIcon, CheckIcon, VideoIcon, ShieldIcon,
} from '../assets/icons/Icons';
import '../styles/auth.css';

const getPasswordStrength = (password) => {
  if (!password) return { level: 0, label: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { level: 1, label: 'Weak', class: 'weak' };
  if (score <= 2) return { level: 2, label: 'Medium', class: 'medium' };
  if (score <= 3) return { level: 3, label: 'Good', class: 'medium' };
  return { level: 4, label: 'Strong', class: 'strong' };
};

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    role: 'patient',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    regNumber: '',
    specialization: '',
    agreed: false,
  });

  const isDoctor = form.role === 'doctor';
  const setRole = (role) => setForm((prev) => ({ ...prev, role }));

  const strength = useMemo(
    () => getPasswordStrength(form.password),
    [form.password]
  );

  const updateField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: connect to Django auth API
    console.log('Signup:', form);
  };

  return (
    <div className="auth-page">

      {/* Left Panel — Decorative */}
      <div className="auth-panel">
        <div className="auth-panel-header">
          <Link to="/" className="logo">
            <div className="logo-icon">
              <HeartIcon size={20} color="white" />
            </div>
            <span className="logo-text">Ishira</span>
          </Link>
        </div>

        <div className="auth-panel-content">
          <h1>Begin your journey to <em>inner peace</em></h1>
          <p>
            Join thousands who've found clarity, support, and lasting
            change through Ishira's expert psychiatric care.
          </p>
        </div>

        <div className="auth-panel-features">
          <div className="auth-feature">
            <div className="auth-feature-icon">
              <CheckIcon size={16} />
            </div>
            <span>Free 15-minute initial consultation</span>
          </div>
          <div className="auth-feature">
            <div className="auth-feature-icon">
              <ShieldIcon size={16} />
            </div>
            <span>HIPAA compliant &amp; ISO 27001 certified</span>
          </div>
          <div className="auth-feature">
            <div className="auth-feature-icon">
              <VideoIcon size={16} color="white" />
            </div>
            <span>Online and in-person appointments</span>
          </div>
        </div>
      </div>

      {/* Right Panel — Signup Form */}
      <div className="auth-form-panel">
        <div className="auth-form-container">

          <div className="auth-form-header">
            <h2>Create your account</h2>
            {/* Phase-1 launch: login disabled — cross-link hidden */}
            {/* <p>
              Already have an account?{' '}
              <Link to="/login">Sign in</Link>
            </p> */}
          </div>

          {/* Role Toggle */}
          <div className="role-toggle" role="tablist" aria-label="Account type">
            <button
              type="button"
              role="tab"
              aria-selected={!isDoctor}
              className={`role-option ${!isDoctor ? 'active' : ''}`}
              onClick={() => setRole('patient')}
            >
              <UserIcon size={16} />
              I'm a patient
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isDoctor}
              className={`role-option ${isDoctor ? 'active' : ''}`}
              onClick={() => setRole('doctor')}
            >
              <ShieldIcon size={16} />
              I'm a doctor
            </button>
          </div>

          {/* Social Signup */}
          <div className="auth-social">
            <button className="social-btn" type="button">
              <GoogleIcon size={18} />
              Sign up with Google
            </button>
          </div>

          <div className="auth-divider">
            <span>or sign up with email</span>
          </div>

          {/* Signup Form */}
          <form className="auth-form" onSubmit={handleSubmit}>

            {/* Name Row */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="firstName">First name</label>
                <div className="form-input-wrap">
                  <input
                    id="firstName"
                    type="text"
                    className="form-input"
                    placeholder="First name"
                    value={form.firstName}
                    onChange={updateField('firstName')}
                    required
                    autoComplete="given-name"
                  />
                  <span className="input-icon">
                    <UserIcon size={16} />
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="lastName">Last name</label>
                <div className="form-input-wrap">
                  <input
                    id="lastName"
                    type="text"
                    className="form-input no-icon"
                    placeholder="Last name"
                    value={form.lastName}
                    onChange={updateField('lastName')}
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label className="form-label" htmlFor="signup-email">Email address</label>
              <div className="form-input-wrap">
                <input
                  id="signup-email"
                  type="email"
                  className="form-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={updateField('email')}
                  required
                  autoComplete="email"
                />
                <span className="input-icon">
                  <MailIcon size={16} />
                </span>
              </div>
            </div>

            {/* Phone */}
            <div className="form-group">
              <label className="form-label" htmlFor="phone">Phone number</label>
              <div className="form-input-wrap">
                <input
                  id="phone"
                  type="tel"
                  className="form-input"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={updateField('phone')}
                  required
                  autoComplete="tel"
                />
                <span className="input-icon">
                  <PhoneIcon size={16} color="currentColor" />
                </span>
              </div>
            </div>

            {/* Doctor-only fields */}
            {isDoctor && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="reg-number">
                    Medical registration number
                  </label>
                  <div className="form-input-wrap">
                    <input
                      id="reg-number"
                      type="text"
                      className="form-input"
                      placeholder="e.g., MCI-123456"
                      value={form.regNumber}
                      onChange={updateField('regNumber')}
                      required
                    />
                    <span className="input-icon">
                      <ShieldIcon size={16} />
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="specialization">
                    Specialization
                  </label>
                  <div className="form-input-wrap">
                    <select
                      id="specialization"
                      className="form-input form-select no-icon"
                      value={form.specialization}
                      onChange={updateField('specialization')}
                      required
                    >
                      <option value="">Select your specialization</option>
                      <option value="psychiatry">Psychiatry</option>
                      <option value="clinical-psychology">Clinical Psychology</option>
                      <option value="counselling">Counselling Psychology</option>
                      <option value="child-adolescent">Child &amp; Adolescent Psychiatry</option>
                      <option value="addiction">Addiction Psychiatry</option>
                      <option value="neuropsychiatry">Neuropsychiatry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="auth-note">
                  Your credentials will be reviewed by our team before your
                  account is activated. We'll be in touch within 1–2 business days.
                </div>
              </>
            )}

            {/* Password */}
            <div className="form-group">
              <label className="form-label" htmlFor="signup-password">Password</label>
              <div className="form-input-wrap">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Min 8 characters"
                  value={form.password}
                  onChange={updateField('password')}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <span className="input-icon">
                  <LockIcon size={16} />
                </span>
                <button
                  type="button"
                  className="input-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                </button>
              </div>

              {/* Password Strength Meter */}
              {form.password && (
                <>
                  <div className="password-strength">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`strength-bar ${
                          i <= strength.level ? `active ${strength.class}` : ''
                        }`}
                      />
                    ))}
                  </div>
                  <div className={`strength-text ${strength.class}`}>
                    {strength.label}
                  </div>
                </>
              )}
            </div>

            {/* Terms Agreement */}
            <div className="form-checkbox">
              <input
                type="checkbox"
                id="terms"
                checked={form.agreed}
                onChange={updateField('agreed')}
                required
              />
              <label htmlFor="terms">
                I agree to the <a href="#">Terms of Service</a> and{' '}
                <a href="#">Privacy Policy</a>
              </label>
            </div>

            <button type="submit" className="auth-submit">
              {isDoctor ? 'Apply to join Ishira' : 'Create account'}
            </button>

          </form>

        </div>
      </div>

    </div>
  );
};

export default Signup;
